import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Beaker, Clock, Play, FileText, ChevronRight, Activity,
  Microscope, Dna, Home, Send, Upload, ChevronLeft,
  BarChart, Download, Edit, Printer, X, Plus, Menu
} from 'lucide-react';
import Header from '../components/Header';
import '../bioframe-spa-styles.css';

// Main App Component
const App = () => {
  return (
    <Router>
      <div className="app">
        <Routes>
          <Route path="/" element={<LandingScreen />} />
          <Route path="/chat" element={<ChatScreen />} />
          <Route path="/protocol" element={<ProtocolScreen />} />
          <Route path="/upload" element={<UploadScreen />} />
          <Route path="/analysis" element={<AnalysisScreen />} />
          <Route path="/report" element={<ReportScreen />} />
        </Routes>
      </div>
    </Router>
  );
};

// Header moved to src/components/Header.jsx

// Landing Screen Component
const LandingScreen = () => {
  const navigate = useNavigate();
  const [hoveredExperiment, setHoveredExperiment] = useState(null);

  const recentExperiments = [
    {
      id: 1,
      title: "BBB Permeability Study",
      modified: "2 hours ago",
      status: "running",
      chipType: "Emulate Brain-Chip",
      assays: ["TEER", "IF Imaging"],
      progress: 65
    },
    {
      id: 2,
      title: "Lung Inflammation Response",
      modified: "Yesterday",
      status: "analyzed",
      chipType: "CN Bio PhysioMimix",
      assays: ["Cytokine Panel", "Live/Dead"],
      progress: 100
    },
    {
      id: 3,
      title: "Intestinal Barrier Function",
      modified: "3 days ago",
      status: "draft",
      chipType: "Mimetas OrganoPlate",
      assays: ["TEER", "Permeability", "RNA-seq"],
      progress: 0
    }
  ];

  const getStatusColor = (status) => {
    switch(status) {
      case 'running': return 'status-running';
      case 'analyzed': return 'status-analyzed';
      case 'draft': return 'status-draft';
      default: return 'status-draft';
    }
  };

  return (
    <div className="landing-screen">
      <Header />
      
      <div className="main-container">
        <div className="hero-section">
          <div className="hero-content">
            <div className="logo-container">
              <div className="logo">
                <Dna size={32} />
              </div>
              <span className="logo-text">Bioframe</span>
            </div>

            <h1 className="hero-title">Design your next experiment with AI</h1>
            <p className="hero-description">
              Describe your study in plain language. Bioframe generates protocols,
              analyzes multimodal data, and produces publication-ready reports.
            </p>

            <div className="cta-panel">
              <button className="cta-button" onClick={() => navigate('/chat')}>
                <Beaker size={20} />
                <span>Start New Experiment</span>
                <ChevronRight size={20} />
              </button>
              <p className="cta-subtext">
                No coding required • TEER, imaging, and omics support • Export to PDF
              </p>
            </div>

            <div className="feature-pills">
              <div className="pill blue">
                <Activity size={16} />
                <span>Signal Analysis</span>
              </div>
              <div className="pill green">
                <Microscope size={16} />
                <span>Imaging Pipeline</span>
              </div>
              <div className="pill purple">
                <Dna size={16} />
                <span>Omics Integration</span>
              </div>
            </div>
          </div>
        </div>

        <div className="sidebar">
          <h2 className="sidebar-title">Recent Experiments</h2>
          <div className="experiments-container">
            {recentExperiments.map((exp) => (
              <div
                key={exp.id}
                className={`experiment-card ${hoveredExperiment === exp.id ? 'expanded' : ''}`}
                onMouseEnter={() => setHoveredExperiment(exp.id)}
                onMouseLeave={() => setHoveredExperiment(null)}
                onClick={() => navigate('/analysis')}
              >
                <div className="experiment-header">
                  <div className="experiment-info">
                    <h3 className="experiment-title">{exp.title}</h3>
                    <p className="experiment-time">{exp.modified}</p>
                  </div>
                  <span className={`status-badge ${getStatusColor(exp.status)}`}>
                    {exp.status === 'running' && <Play size={12} />}
                    {exp.status === 'analyzed' && <FileText size={12} />}
                    {exp.status === 'draft' && <Clock size={12} />}
                    {exp.status}
                  </span>
                </div>
                
                {hoveredExperiment === exp.id && (
                  <div className="expanded-details">
                    <div className="detail-row">
                      <span className="detail-label">Chip:</span>
                      <span>{exp.chipType}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Assays:</span>
                      <div className="assay-tags">
                        {exp.assays.map((assay, i) => (
                          <span key={i} className="assay-tag">{assay}</span>
                        ))}
                      </div>
                    </div>
                    {exp.status === 'running' && (
                      <div className="progress-container">
                        <div className="progress-header">
                          <span>Progress</span>
                          <span>{exp.progress}%</span>
                        </div>
                        <div className="experiment-progress-bar">
                          <div className="progress-fill" style={{ width: `${exp.progress}%` }} />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Chat Screen Component
const ChatScreen = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'ai',
      text: "Hello! I'm ready to help you design your organ-on-chip experiment. Please describe what you'd like to study, and I'll generate a complete protocol for you.",
      quickActions: ['BBB permeability study', 'Drug toxicity screening', 'Inflammation response']
    },
    {
      id: 2,
      type: 'user',
      text: "I want to study the effect of inflammatory cytokines on blood-brain barrier integrity using an Emulate Brain-Chip with primary human brain microvascular endothelial cells."
    },
    {
      id: 3,
      type: 'ai',
      text: "Excellent! I'm structuring your BBB inflammation study. I see you're using the Emulate Brain-Chip with primary BMECs. Let me clarify a few details:",
      quickActions: ['Add astrocyte co-culture?', 'Include pericytes?', 'Continuous TEER monitoring?']
    }
  ]);
  
  const [inputValue, setInputValue] = useState('');
  const [protocolData, setProtocolData] = useState({
    chipType: 'Emulate Brain-Chip',
    cellTypes: ['Primary human BMECs'],
    assays: ['TEER monitoring', 'Cytokine analysis', 'IF imaging'],
    flow: '',
    duration: ''
  });

  const sendMessage = () => {
    if (!inputValue.trim()) return;
    
    setMessages([...messages, {
      id: messages.length + 1,
      type: 'user',
      text: inputValue
    }]);
    
    setInputValue('');
    
    // Simulate AI response
    setTimeout(() => {
      setMessages(prev => [...prev, {
        id: prev.length + 1,
        type: 'ai',
        text: "I've updated the protocol based on your input. The parameters look good for your study."
      }]);
    }, 1000);
  };

  return (
    <div className="chat-screen">
      <Header title="New Experiment" showProgress currentStep={2} />
      
      <div className="chat-container">
        <div className="chat-panel">
          <div className="chat-header">
            <h2>Describe Your Experiment</h2>
            <p>Tell me about your study in plain language</p>
          </div>
          
          <div className="messages-container">
            {messages.map((msg) => (
              <div key={msg.id} className={`message ${msg.type}`}>
                <div className="message-avatar">
                  {msg.type === 'ai' ? 'AI' : 'JS'}
                </div>
                <div className="message-content">
                  <p>{msg.text}</p>
                  {msg.quickActions && (
                    <div className="quick-actions">
                      {msg.quickActions.map((action, i) => (
                        <button key={i} className="quick-action">
                          {action}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          <div className="input-container">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Type your message..."
              className="message-input"
            />
            <button onClick={sendMessage} className="send-button">
              <Send size={20} />
            </button>
          </div>
        </div>
        
        <div className="preview-panel">
          <h3>Live Protocol Preview</h3>
          
          <div className="protocol-card">
            <div className="protocol-icon chip">
              <Beaker size={16} />
            </div>
            <span className="protocol-label">Chip Type</span>
            <div className="protocol-value">{protocolData.chipType}</div>
          </div>
          
          <div className="protocol-card">
            <div className="protocol-icon cells">
              <Microscope size={16} />
            </div>
            <span className="protocol-label">Cell Types</span>
            <div className="protocol-value">
              {protocolData.cellTypes.map((cell, i) => (
                <span key={i} className="protocol-tag">{cell}</span>
              ))}
            </div>
          </div>
          
          <div className="protocol-card">
            <div className="protocol-icon assays">
              <Activity size={16} />
            </div>
            <span className="protocol-label">Assays</span>
            <div className="protocol-value">
              {protocolData.assays.map((assay, i) => (
                <span key={i} className="protocol-tag">{assay}</span>
              ))}
            </div>
          </div>
          
          <button 
            className="btn-generate"
            onClick={() => navigate('/protocol')}
          >
            Generate Protocol
          </button>
        </div>
      </div>
    </div>
  );
};

// Protocol Review Screen Component
const ProtocolScreen = () => {
  const navigate = useNavigate();
  const [showJson, setShowJson] = useState(false);
  const [phases] = useState([
    {
      id: 1,
      name: 'Cell Seeding',
      day: 'Day 0',
      description: 'Seed primary human BMECs on apical channel at 10M cells/ml.',
      tasks: [
        'Coat channels with fibronectin (100 µg/ml)',
        'Prepare cell suspension in EGM-2',
        'Seed cells and incubate static for 2h'
      ],
      color: 'seeding'
    },
    {
      id: 2,
      name: 'Flow Initiation',
      day: 'Day 1-3',
      description: 'Establish physiological flow conditions and allow barrier formation.',
      tasks: [
        'Start flow at 30 µl/h, increase to 100 µl/h',
        'Daily TEER measurements',
        'Media change every 48h'
      ],
      color: 'perfusion'
    },
    {
      id: 3,
      name: 'Cytokine Treatment',
      day: 'Day 4-5',
      description: 'Apply inflammatory cytokines and monitor barrier disruption.',
      tasks: [
        'Add cytokines (10 ng/ml each)',
        'TEER measurements every 6h',
        'Collect effluent samples'
      ],
      color: 'treatment'
    },
    {
      id: 4,
      name: 'Recovery & Analysis',
      day: 'Day 6-7',
      description: 'Remove treatment, monitor recovery, perform endpoint analyses.',
      tasks: [
        'Replace with fresh media',
        'Continue TEER monitoring',
        'Fix cells for IF staining'
      ],
      color: 'recovery'
    }
  ]);

  return (
    <div className="protocol-screen">
      <Header title="Protocol Review" showProgress currentStep={3} />
      
      <div className="protocol-container">
        <div className="timeline-panel">
          <h2>Experimental Timeline</h2>
          
          <div className="timeline">
            {phases.map((phase) => (
              <div key={phase.id} className={`phase-card ${phase.color}`}>
                <div className="phase-indicator" />
                <div className="phase-content">
                  <div className="phase-header">
                    <span className="phase-title">{phase.name}</span>
                    <span className="phase-day">{phase.day}</span>
                  </div>
                  <p className="phase-description">{phase.description}</p>
                  <div className="phase-tasks">
                    {phase.tasks.map((task, i) => (
                      <div key={i} className="task">
                        <input type="checkbox" className="task-checkbox" />
                        <span>{task}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="parameters-panel">
          <h2>Protocol Parameters</h2>
          
          <div className="parameter-section">
            <label>Chip Model</label>
            <select className="parameter-input">
              <option>Emulate Brain-Chip</option>
              <option>CN Bio PhysioMimix Neuro</option>
              <option>Mimetas OrganoPlate BBB</option>
            </select>
          </div>
          
          <div className="parameter-section">
            <label>Flow Rate</label>
            <div className="flow-inputs">
              <input type="number" defaultValue="100" className="parameter-input" />
              <span>µl/h</span>
            </div>
          </div>
          
          <div className="parameter-section">
            <label>Culture Media</label>
            <input type="text" defaultValue="EGM-2 BulletKit (Lonza CC-3162)" className="parameter-input" />
          </div>
          
          <div className="parameter-section">
            <label>Sampling Schedule</label>
            <div className="schedule-builder">
              <input type="text" defaultValue="Day 0: Baseline" className="parameter-input" />
              <input type="text" defaultValue="Day 1-3: Daily TEER" className="parameter-input" />
              <input type="text" defaultValue="Day 4-5: 6h intervals" className="parameter-input" />
            </div>
          </div>
        </div>
      </div>
      
      <div className="bottom-actions">
        <div className="json-toggle">
          <span>View JSON</span>
          <button 
            className={`toggle-switch ${showJson ? 'active' : ''}`}
            onClick={() => setShowJson(!showJson)}
          />
        </div>
        <div className="action-buttons">
          <button className="btn btn-secondary" onClick={() => navigate('/chat')}>
            Back to Chat
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/upload')}>
            Continue to Data Upload
          </button>
        </div>
      </div>
      
      {showJson && (
        <div className="json-view">
          <pre>{JSON.stringify({
            protocol: {
              title: "BBB Inflammation Study",
              chip_type: "Emulate Brain-Chip",
              duration_days: 7,
              phases: phases.map(p => ({
                name: p.name,
                day: p.day,
                tasks: p.tasks
              }))
            }
          }, null, 2)}</pre>
        </div>
      )}
    </div>
  );
};

// Upload Screen Component
const UploadScreen = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState([
    {
      id: 1,
      name: 'TEER_measurements_day0-7.csv',
      size: '2.4 MB',
      type: 'TEER Data',
      status: 'valid',
      phase: 'All phases'
    },
    {
      id: 2,
      name: 'ZO1_IF_day7.tiff',
      size: '45.8 MB',
      type: 'IF Imaging',
      status: 'processing',
      phase: 'Recovery'
    },
    {
      id: 3,
      name: 'Claudin5_IF_day7.tiff',
      size: '42.3 MB',
      type: 'IF Imaging',
      status: 'valid',
      phase: 'Recovery'
    }
  ]);
  
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    // Handle file upload
  };

  return (
    <div className="upload-screen">
      <Header title="Data Upload" showProgress currentStep={4} />
      
      <div className="upload-container">
        <div className="upload-panel">
          <div className="upload-header">
            <h1>Upload Your Experimental Results</h1>
            <p>Drag and drop your data files or click to browse</p>
          </div>
          
          <div 
            className={`dropzone ${dragOver ? 'dragover' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            <Upload size={48} />
            <p>Drop files here or click to upload</p>
            <p className="upload-hint">Support for CSV, XLS, TIFF, JPG, ND2, FASTQ files up to 500MB</p>
          </div>
          
          <div className="file-types">
            <div className="file-type teer">
              <Activity size={24} />
              <span>TEER / Impedance</span>
              <small>CSV, XLS</small>
            </div>
            <div className="file-type imaging">
              <Microscope size={24} />
              <span>Imaging</span>
              <small>TIFF, JPG, ND2</small>
            </div>
            <div className="file-type omics">
              <Dna size={24} />
              <span>Omics</span>
              <small>FASTQ, CSV</small>
            </div>
          </div>
          
          <div className="files-section">
            <h2>Uploaded Files</h2>
            
            <table className="files-table">
              <thead>
                <tr>
                  <th>Filename</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Phase</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {files.map((file) => (
                  <tr key={file.id}>
                    <td>
                      <div className="file-name">
                        <FileText size={20} />
                        <div>
                          <div className="file-title">{file.name}</div>
                          <div className="file-size">{file.size}</div>
                        </div>
                      </div>
                    </td>
                    <td>{file.type}</td>
                    <td>
                      <span className={`status-badge ${file.status}`}>
                        {file.status === 'valid' && '✓ Valid'}
                        {file.status === 'processing' && 'Processing...'}
                      </span>
                    </td>
                    <td>
                      <select defaultValue={file.phase}>
                        <option>All phases</option>
                        <option>Seeding</option>
                        <option>Perfusion</option>
                        <option>Treatment</option>
                        <option>Recovery</option>
                      </select>
                    </td>
                    <td>
                      <button 
                        className="btn-remove"
                        onClick={() => setFiles(files.filter(f => f.id !== file.id))}
                      >
                        <X size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="upload-actions">
            <button className="btn btn-secondary" onClick={() => navigate('/protocol')}>
              Back to Protocol
            </button>
            <button className="btn btn-primary" onClick={() => navigate('/analysis')}>
              Run Analysis
            </button>
          </div>
        </div>
        
        <div className="sidebar">
          <div className="expected-data">
            <h3>Expected Data Map</h3>
            <div className="expected-list">
              <div className="expected-item">
                <div className="expected-check complete">✓</div>
                <div>
                  <div className="expected-label">Day 0-7 TEER traces</div>
                  <div className="expected-description">Continuous impedance measurements</div>
                </div>
              </div>
              <div className="expected-item">
                <div className="expected-check complete">✓</div>
                <div>
                  <div className="expected-label">3 IF images</div>
                  <div className="expected-description">ZO-1, Claudin-5, Occludin staining</div>
                </div>
              </div>
              <div className="expected-item">
                <div className="expected-check"></div>
                <div>
                  <div className="expected-label">Cytokine measurements</div>
                  <div className="expected-description">TNF-α, IL-1β, IL-6 levels</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Analysis Screen Component
const AnalysisScreen = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const metrics = [
    { label: 'ΔTEER vs Baseline', value: '-68%', change: 'negative', description: 'Significant disruption detected' },
    { label: 'Barrier Recovery', value: '42 hrs', change: 'positive', description: '85% recovery at endpoint' },
    { label: 'TJ Integrity Score', value: '7.2/10', change: 'neutral', description: 'Moderate preservation' },
    { label: 'Peak Cytokines', value: '18 hrs', change: 'neutral', description: 'Post-treatment' }
  ];

  return (
    <div className="analysis-screen">
      <Header title="Analysis Results" showProgress currentStep={5} />
      
      <div className="tabs-container">
        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={`tab ${activeTab === 'teer' ? 'active' : ''}`}
            onClick={() => setActiveTab('teer')}
          >
            TEER Analysis
          </button>
          <button 
            className={`tab ${activeTab === 'imaging' ? 'active' : ''}`}
            onClick={() => setActiveTab('imaging')}
          >
            Imaging
          </button>
          <button 
            className={`tab ${activeTab === 'summary' ? 'active' : ''}`}
            onClick={() => setActiveTab('summary')}
          >
            Summary Report
          </button>
        </div>
      </div>
      
      <div className="analysis-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            <div className="metrics-grid">
              {metrics.map((metric, i) => (
                <div key={i} className="metric-card">
                  <div className="metric-header">
                    <span className="metric-label">{metric.label}</span>
                    <div className={`metric-icon ${metric.change}`}>
                      <BarChart size={20} />
                    </div>
                  </div>
                  <div className="metric-value">{metric.value}</div>
                  <div className={`metric-change ${metric.change}`}>
                    {metric.description}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="chart-container">
              <h2>TEER Timeline with Treatment Phases</h2>
              <div className="chart-area">
                <div className="chart-placeholder">
                  {/* Chart visualization would go here */}
                  <BarChart size={48} />
                  <p>Interactive chart visualization</p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'teer' && (
          <div className="teer-tab">
            <div className="chart-container">
              <h2>Transepithelial Electrical Resistance</h2>
              <div className="chart-controls">
                <button className="chart-control">Raw</button>
                <button className="chart-control active">Smoothed</button>
                <button className="chart-control">Normalized</button>
              </div>
              <div className="chart-area">
                <div className="chart-placeholder">
                  <Activity size={48} />
                  <p>TEER measurements over time</p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'imaging' && (
          <div className="imaging-tab">
            <div className="imaging-grid">
              {['ZO-1 Staining', 'Claudin-5 Staining', 'F-Actin Organization'].map((title, i) => (
                <div key={i} className="image-card">
                  <div className="image-preview">
                    <Microscope size={48} />
                    <p>{title} - Day 7</p>
                  </div>
                  <div className="image-info">
                    <div className="image-metrics">
                      <span>Continuity: 78%</span>
                      <span>Intensity: 156 AU</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {activeTab === 'summary' && (
          <div className="summary-tab">
            <div className="summary-panel">
              <div className="ai-badge">
                AI Generated
              </div>
              <h2>Experimental Interpretation</h2>
              
              <div className="summary-content">
                <p>
                  The blood-brain barrier model demonstrated a robust inflammatory response following cytokine treatment, 
                  with TEER values dropping by 68% from baseline within 12 hours of TNF-α and IL-1β administration.
                </p>
                <p>
                  Barrier recovery began approximately 18 hours post-treatment removal, with TEER values recovering to 
                  85% of baseline by day 7. The recovery kinetics suggest reversible tight junction disruption.
                </p>
                
                <div className="key-findings">
                  <h3>Key Findings</h3>
                  <ul>
                    <li>Significant but reversible barrier disruption</li>
                    <li>Recovery initiated within 24 hours</li>
                    <li>Tight junction proteins maintained integrity</li>
                    <li>Model successfully recapitulates neuroinflammation</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <div className="analysis-actions">
        <button className="btn btn-secondary" onClick={() => navigate('/upload')}>
          Back to Upload
        </button>
        <button className="btn btn-primary" onClick={() => navigate('/report')}>
          Generate Report
        </button>
      </div>
    </div>
  );
};

// Report Screen Component
const ReportScreen = () => {
  const navigate = useNavigate();
  const [exportFormat, setExportFormat] = useState('pdf');

  return (
    <div className="report-screen">
      <Header title="Final Report" showProgress currentStep={6} />
      
      <div className="report-container">
        <div className="report-page cover-page">
          <div className="cover-logo">
            <Dna size={40} />
          </div>
          
          <h1>Blood-Brain Barrier Inflammation Study</h1>
          <p className="cover-subtitle">Cytokine-Induced Barrier Disruption and Recovery Analysis</p>
          
          <div className="cover-metadata">
            <div className="metadata-item">
              <span className="metadata-label">Date</span>
              <span className="metadata-value">November 11, 2025</span>
            </div>
            <div className="metadata-item">
              <span className="metadata-label">Researcher</span>
              <span className="metadata-value">J. Smith</span>
            </div>
            <div className="metadata-item">
              <span className="metadata-label">Chip Type</span>
              <span className="metadata-value">Emulate Brain-Chip</span>
            </div>
            <div className="metadata-item">
              <span className="metadata-label">Duration</span>
              <span className="metadata-value">7 Days</span>
            </div>
          </div>
        </div>
        
        <div className="report-page content-page">
          <section>
            <h2>1. Protocol Summary</h2>
            <p>
              This study investigated the effects of inflammatory cytokines on blood-brain barrier integrity 
              using the Emulate Brain-Chip platform. Primary human brain microvascular endothelial cells 
              were cultured under physiological flow conditions for 7 days.
            </p>
          </section>
          
          <section>
            <h2>2. Results & Analysis</h2>
            <p>
              Transepithelial electrical resistance measurements demonstrated significant barrier disruption 
              following cytokine treatment. TEER values decreased by 68% from baseline within 12 hours.
            </p>
            
            <div className="key-results">
              <h3>Key Metrics</h3>
              <ul>
                <li>Maximum TEER reduction: 68% below baseline</li>
                <li>Time to minimum TEER: 18 hours post-treatment</li>
                <li>Recovery to 85% baseline: 42 hours</li>
                <li>Final TEER recovery: 85% of initial values</li>
              </ul>
            </div>
          </section>
          
          <section>
            <h2>3. Biological Interpretation</h2>
            <p>
              The observed barrier disruption pattern is consistent with cytokine-mediated tight junction 
              reorganization. The reversible nature suggests no permanent cellular damage occurred.
            </p>
          </section>
        </div>
      </div>
      
      <div className="export-bar">
        <div className="export-options">
          <span>Export Format:</span>
          <div className="format-buttons">
            {['PDF', 'Word', 'LaTeX', 'JSON'].map((format) => (
              <button
                key={format}
                className={`format-btn ${exportFormat === format.toLowerCase() ? 'active' : ''}`}
                onClick={() => setExportFormat(format.toLowerCase())}
              >
                {format}
              </button>
            ))}
          </div>
        </div>
        <div className="export-actions">
          <button className="btn btn-secondary" onClick={() => navigate('/analysis')}>
            Back to Analysis
          </button>
          <button className="btn btn-primary">
            <Download size={16} />
            Export Report
          </button>
        </div>
      </div>
    </div>
  );
};

export default App;
